import { BaseDeviceInfo } from "@/data/models/BaseDeviceInfo";
import { NewGenericDevice } from "@/data/models/GenericDevice";
import Vue from "vue";
import { formatNumber } from "./Utils";

//This can be expanded later to include availability flags from 1 to 9
enum DeviceAvailability {
  DELETED = -1,
  AVAILABLE = 0,
  NOT_AVAILABLE = 1,
}

export function filterRetainedDevices<T extends BaseDeviceInfo>(devices: T[]) {
  return devices.filter((device) => device.availability !== -1);
}

export function getNumberOfRetainedDevices<T extends BaseDeviceInfo>(devices: T[]) {
  return filterRetainedDevices(devices).length;
}

export function sortDevices<T extends BaseDeviceInfo>(devices: T[]) {
  return devices.sort((a, b) => {
    if (a.serialNumber && b.serialNumber) {
      return a.serialNumber.localeCompare(b.serialNumber);
    }

    return a.inventoryNumber.localeCompare(b.inventoryNumber);
  });
}

export function isDeviceDeleted<T extends BaseDeviceInfo>(device: T) {
  return device.availability === DeviceAvailability.DELETED;
}

export function isDeviceAvailable<T extends BaseDeviceInfo>(device: T) {
  return !device.availability || device.availability === DeviceAvailability.AVAILABLE;
}

/**
 * Generates a formatted string for displaying device counts with localization support.
 * This function takes a numeric count and a translation key, formats the number, retrieves the localized
 * translation string, and replaces the numeric placeholder with the formatted number. This is particularly
 * useful for displaying counts in a user-friendly format within a UI, considering the localization practices.
 *
 * @param {Vue} vue - The Vue instance used to access the localization method.
 * @param {number} count - The number of devices to be formatted and displayed.
 * @param {string} translationKey - The key used to retrieve the localized string from Vue's translation system.
 * @returns {string} A formatted string with the count properly localized and inserted.
 */
export function getFormattedDeviceString(vue: Vue, count: number, translationKey: string): string {
  const formattedCount = formatNumber(count);
  // Get the full translation string with count placeholder replaced by the actual count
  const fullTranslation = vue.$tc(translationKey, count, { count });

  // Split the translation to identify where the 'count' appears
  const words = fullTranslation.split(" ");

  // Find the index of the count within the translation
  const countIndex = words.findIndex(word => word.includes(count.toString()));

  // Replace the specific occurrence of the count with the formatted number
  if (countIndex !== -1) {
    words[countIndex] = formattedCount;
  }

  // Join the words back into a single string
  return words.join(" ");
}

export function shouldDeleteDevice(updatedDevice: NewGenericDevice) {
  return updatedDevice.availability === DeviceAvailability.DELETED;
}


// TO BE USED LATER
export function shouldRestoreDevice<T extends BaseDeviceInfo>(device: T) {
  if (!device.availability) {
    return false;
  }

  return (device.availability !== DeviceAvailability.DELETED && 
    device.availability >= 0 && device.availability <= 9);
}